/**
 * Tab Event Handlers
 * Handles click, shown, hidden events for tabs
 */

class TabHandlers {
    constructor(stateManager, eventTrigger, callbacks) {
        this.state = stateManager;
        this.triggerEvent = eventTrigger;
        this.callbacks = callbacks || {};
    }
    
    /**
     * Handle main tab click
     */
    handleTabClick(event, tabId) {
        try {
            let tabElement = event.currentTarget;
            if (!tabElement || !tabElement.getAttribute) {
                tabElement = event.target.closest ? event.target.closest('button[tab-link]') : null;
                if (!tabElement) {
                    tabElement = document.getElementById(tabId);
                }
            }
            
            if (!tabElement) {
                if (debugMode === '1') {
                    console.warn('[TabHandlers] Could not find tab element for tabId:', tabId);
                }
                return;
            }
            
            const tabLink = tabElement.getAttribute('tab-link');
            const wasActiveBeforeClick = tabElement.getAttribute('data-was-active-before-click') === 'true';
            const hasActiveClass = tabElement.classList.contains('active');
            const hasAriaSelected = tabElement.getAttribute('aria-selected') === 'true';
            const isCurrentlyActiveInDOM = hasActiveClass || hasAriaSelected;
            const wasAlreadyActive = wasActiveBeforeClick;
            
            
            // Check fulltext search
            if (tabLink === 'fulltext') {
                // Skip automatic search if we're restoring state from browser history
                // The browser history handler will handle the search
                if (window.__isRestoringBrowserState) {
                    return;
                }
                
                let hasResults = false;
                let isLoading = false;
                
                if (window.appInitializationManager && typeof window.appInitializationManager.hasFulltextResults === 'function') {
                    hasResults = window.appInitializationManager.hasFulltextResults();
                } else {
                    const $container = $('#nav-full-text');
                    const $results = $container.find('.search-result-item');
                    const $spinner = $container.find('.fulltext-loading-spinner');
                    hasResults = $results.length > 0;
                    isLoading = $spinner.length > 0 && !$spinner.hasClass('d-none');
                }
                
                const shouldRunSearch = (!hasResults && !isLoading) || (hasResults && wasAlreadyActive);
                
                if (shouldRunSearch && window.searchApi && window.axios) {
                    
                    let query = '';
                    const searchInputs = [
                        document.getElementById('fnd_searchbox'),
                        document.querySelector('input[name="sq"]'),
                        document.querySelector('input[name="search"]'),
                        document.querySelector('input[type="search"]')
                    ].filter(Boolean);
                    
                    if (searchInputs.length > 0) {
                        query = (searchInputs[0].value || '').toString().trim();
                    }
                    
                    if (!query && window.QUERY) {
                        query = (window.QUERY || '').toString().trim();
                    }
                    
                    if (!query) {
                        try {
                            const urlParams = new URLSearchParams(window.location.search);
                            query = (urlParams.get('sq') || '').toString().trim();
                        } catch(_) {}
                    }
                    
                    // Only proceed if query is not empty (after trim)
                    if (query && query.trim()) {
                        window.QUERY = query;
                        
                        if (window.filterController) {
                            window.filterController.activeFilters.searchQuery = query;
                        }
                        
                        if (window.appInitializationManager && typeof window.appInitializationManager.performFulltextSearch === 'function') {
                            window.appInitializationManager.performFulltextSearch(query);
                        } else if (window.performFulltextSearch && typeof window.performFulltextSearch === 'function') {
                            window.performFulltextSearch(query);
                        } else {
                            if (window.HyperLog) {
                                window.HyperLog.error('[TabHandlers] Fulltext tab click - NO SEARCH FUNCTION AVAILABLE!');
                            }
                        }
                    }
                }
            }
            
            this.state.addToHistory(tabId, tabLink);
            
            if (tabLink && this.callbacks.updateUrlHash) {
                this.callbacks.updateUrlHash(tabLink);
            }
            
            this.triggerEvent('tab:click', { tabId, tabLink, element: tabElement });
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabHandlers] Tab click handler error:', error);
            }
        }
    }
    
    /**
     * Handle content tab click
     */
    handleContentTabClick(event, tabId) {
        try {
            const tabElement = event.target;
            const targetPane = tabElement.getAttribute('data-bs-target');
            
            this.triggerEvent('content-tab:click', {
                tabId,
                targetPane,
                element: tabElement
            });
            
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabHandlers] Content tab click handler error:', error);
            }
        }
    }
    
    /**
     * Handle tab shown event
     */
    handleTabShown(event, tabId) {
        try {
            const tabElement = event.target;
            const targetPane = tabElement.getAttribute('data-bs-target');
            const tabLink = tabElement.getAttribute('tab-link');
            
            this.state.updateTabState(tabId, {
                isActive: true,
                lastShown: Date.now()
            });
            
            this.state.setPreviousActiveTabId(tabId);
            
            if (targetPane && this.callbacks.loadTabContent) {
                this.callbacks.loadTabContent(tabId, targetPane);
            }
            
            if (tabLink === 'fulltext') {
                // Skip automatic search if we're restoring state from browser history
                // The browser history handler will handle the search
                if (window.__isRestoringBrowserState) {
                    return;
                }
                
                let hasResults = false;
                let isLoading = false;
                
                if (window.appInitializationManager && typeof window.appInitializationManager.hasFulltextResults === 'function') {
                    hasResults = window.appInitializationManager.hasFulltextResults();
                } else {
                    const $container = $('#nav-full-text');
                    const $results = $container.find('.search-result-item');
                    const $spinner = $container.find('.fulltext-loading-spinner');
                    hasResults = $results.length > 0;
                    isLoading = $spinner.length > 0 && !$spinner.hasClass('d-none');
                }
                
                if (!hasResults && !isLoading && window.searchApi && window.axios) {
                    let query = '';
                    
                    const searchInputs = [
                        document.getElementById('fnd_searchbox'),
                        document.querySelector('input[name="sq"]'),
                        document.querySelector('input[name="search"]'),
                        document.querySelector('input[type="search"]')
                    ].filter(Boolean);
                    
                    if (searchInputs.length > 0) {
                        query = (searchInputs[0].value || '').toString().trim();
                    }
                    
                    if (!query && window.QUERY) {
                        query = (window.QUERY || '').toString().trim();
                    }
                    
                    if (!query) {
                        try {
                            const urlParams = new URLSearchParams(window.location.search);
                            query = (urlParams.get('sq') || '').toString().trim();
                        } catch(_) {}
                    }
                    
                    // Only proceed if query is not empty (after trim)
                    if (query && query.trim()) {
                        window.QUERY = query;
                        
                        if (window.filterController) {
                            window.filterController.activeFilters.searchQuery = query;
                        }
                        
                        if (window.appInitializationManager && typeof window.appInitializationManager.performFulltextSearch === 'function') {
                            window.appInitializationManager.performFulltextSearch(query);
                        } else if (window.performFulltextSearch && typeof window.performFulltextSearch === 'function') {
                            window.performFulltextSearch(query);
                        }
                    }
                }
            }
            
            this.triggerEvent('tab:shown', {
                tabId,
                targetPane,
                element: tabElement
            });
            
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabHandlers] Tab shown handler error:', error);
            }
        }
    }
    
    /**
     * Handle tab hidden event
     */
    handleTabHidden(event, tabId) {
        try {
            const tabElement = event.target;
            
            this.state.updateTabState(tabId, {
                isActive: false,
                lastHidden: Date.now()
            });
            
            this.triggerEvent('tab:hidden', {
                tabId,
                element: tabElement
            });
            
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabHandlers] Tab hidden handler error:', error);
            }
        }
    }
    
    /**
     * Handle content tab shown event
     */
    handleContentTabShown(event, tabId) {
        try {
            const tabElement = event.target;
            const targetPane = tabElement.getAttribute('data-bs-target');
            
            if (targetPane && this.callbacks.loadContentTabData) {
                this.callbacks.loadContentTabData(tabId, targetPane);
            }
            
            this.triggerEvent('content-tab:shown', {
                tabId,
                targetPane,
                element: tabElement
            });
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabHandlers] Content tab shown handler error:', error);
            }
        }
    }
}

export { TabHandlers };

